import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Ban, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import {
  formatKeyValueTargets,
  type KeyValueGroup,
  type KeyValueRow,
} from '@/services/targeting/targeting'
import { KeyValueSearch } from './KeyValueSearch'

export interface KeyValueTargetEditorProps {
  value: KeyValueGroup[]
  onChange: (value: KeyValueGroup[]) => void
}

const emptyRow = (inNotBlock: boolean, key = '', values = ''): KeyValueRow => ({
  key,
  values,
  not: false,
  ...(inNotBlock ? { inNotBlock: true } : {}),
})

const isNotBlock = (row: KeyValueRow) => !!row.inNotBlock

/**
 * Key-value targeting: the key-values an ad request carries. Rows inside a
 * group must all match; each group is an alternative that also matches. Rows
 * in a group's AND NOT block must all fail to match. A row with no values
 * matches any value for its key, and negated with `NOT`, matches when the key
 * carries no value at all.
 */
export function KeyValueTargetEditor({ value, onChange }: KeyValueTargetEditorProps) {
  const { t } = useTranslation()

  /**
   * The rows being edited are held here rather than read back from the saved
   * targeting: a row is only saved once it has a key, so a blank row the user
   * has just added would otherwise vanish the moment it was added.
   */
  const [groups, setGroups] = useState<KeyValueGroup[]>(value)

  // Re-seed when the targeting changes from somewhere else — the pane is
  // cleared, the dialog is reopened. Comparing what each side would save
  // ignores the differences that are ours: blank rows, untrimmed values.
  useEffect(() => {
    setGroups((current) =>
      JSON.stringify(formatKeyValueTargets(current)) === JSON.stringify(formatKeyValueTargets(value))
        ? current
        : value
    )
  }, [value])

  const update = (next: KeyValueGroup[]) => {
    setGroups(next)
    onChange(next)
  }

  const setGroup = (groupIndex: number, rows: KeyValueGroup) =>
    update(
      rows.length === 0
        ? groups.filter((_, gi) => gi !== groupIndex)
        : groups.map((group, gi) => (gi === groupIndex ? rows : group))
    )

  /** Rows are kept in one list per group, positives before the AND NOT block. */
  const setRow = (groupIndex: number, row: KeyValueRow, patch: Partial<KeyValueRow>) =>
    setGroup(
      groupIndex,
      groups[groupIndex].map((r) => (r === row ? { ...r, ...patch } : r))
    )

  const removeRow = (groupIndex: number, row: KeyValueRow) =>
    setGroup(
      groupIndex,
      groups[groupIndex].filter((r) => r !== row)
    )

  const addRow = (groupIndex: number, inNotBlock: boolean, key?: string, values?: string) =>
    setGroup(groupIndex, [...groups[groupIndex], emptyRow(inNotBlock, key, values)])

  const addGroup = (key?: string, values?: string) =>
    update([...groups, [emptyRow(false, key, values)]])

  /**
   * A picked value joins the row being edited when that row is empty or already
   * holds the same key — otherwise it starts an alternative, the way the old UI
   * grows a search result into the targeting.
   */
  const addSearchValue = (key: string, addedValue: string) => {
    const groupIndex = groups.length - 1
    const group: KeyValueGroup | undefined = groups[groupIndex]
    const last = group?.[group.length - 1]
    if (!last || (last.key && last.key !== key)) {
      addGroup(key, addedValue)
      return
    }
    setRow(groupIndex, last, {
      key,
      values: last.values ? `${last.values}, ${addedValue}` : addedValue,
    })
  }

  const copySearchRow = (key: string, values: string[]) => {
    const joined = values.join(', ')
    const groupIndex = groups.length - 1
    const group: KeyValueGroup | undefined = groups[groupIndex]
    const last = group?.[group.length - 1]
    if (!last || last.key) {
      addGroup(key, joined)
      return
    }
    setRow(groupIndex, last, { key, values: joined })
  }

  const renderRow = (groupIndex: number, row: KeyValueRow, indexInBlock: number) => (
    <div key={`${groupIndex}-${groups[groupIndex].indexOf(row)}`} className="flex items-center gap-2">
      <span className="w-14 shrink-0 text-right text-xs font-semibold text-muted-foreground">
        {indexInBlock > 0 ? t('targeting.keyValue.and') : ''}
        {row.not ? ` ${t('targeting.keyValue.not')}` : ''}
      </span>
      <Input
        className="w-40"
        value={row.key}
        placeholder={t('targeting.keyValue.key')}
        onChange={(e) => setRow(groupIndex, row, { key: e.target.value })}
      />
      <Input
        className="flex-1"
        value={row.values}
        placeholder={t('targeting.keyValue.values')}
        onChange={(e) => setRow(groupIndex, row, { values: e.target.value })}
      />
      <Button
        type="button"
        variant={row.not ? 'secondary' : 'ghost'}
        size="icon-sm"
        aria-label={t('targeting.keyValue.negateRow')}
        className={cn(row.not && 'text-(--ad-badge-amber)')}
        onClick={() => setRow(groupIndex, row, { not: !row.not })}
      >
        <Ban className="size-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t('common.remove')}
        onClick={() => removeRow(groupIndex, row)}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  )

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">{t('targeting.keyValue.help')}</p>
      <p className="text-xs text-muted-foreground">{t('targeting.keyValue.anyValueHelp')}</p>

      {groups.length === 0 && (
        <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          {t('targeting.keyValue.empty')}
        </p>
      )}

      {groups.map((group, groupIndex) => {
        const rows = group.filter((row) => !isNotBlock(row))
        const notRows = group.filter(isNotBlock)
        return (
          <div key={groupIndex} className="flex flex-col gap-2">
            {groupIndex > 0 && (
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                {t('targeting.keyValue.or')}
                <span className="h-px flex-1 bg-border" />
              </div>
            )}
            <div className="flex flex-col gap-2 rounded-md border p-3">
              {rows.map((row, index) => renderRow(groupIndex, row, index))}

              {notRows.length > 0 && (
                <div className="flex flex-col gap-2 rounded-md bg-muted/60 p-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase">
                    {rows.length > 0 ? `${t('targeting.keyValue.and')} ` : ''}
                    {t('targeting.keyValue.not')}
                  </span>
                  {notRows.map((row, index) => renderRow(groupIndex, row, index))}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto w-fit p-0 text-xs"
                  onClick={() => addRow(groupIndex, false)}
                >
                  <Plus className="size-3" />
                  {t('targeting.keyValue.addAnd')}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto w-fit p-0 text-xs"
                  onClick={() => addRow(groupIndex, true)}
                >
                  <Plus className="size-3" />
                  {t('targeting.keyValue.addAndNot')}
                </Button>
              </div>
            </div>
          </div>
        )
      })}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        onClick={() => addGroup()}
      >
        <Plus className="size-4" />
        {groups.length === 0 ? t('targeting.keyValue.addRow') : t('targeting.keyValue.addOr')}
      </Button>

      <Separator className="my-1" />

      <h4 className="text-sm font-semibold">{t('targeting.keyValue.searchTitle')}</h4>
      <KeyValueSearch onAddValue={addSearchValue} onCopyRow={copySearchRow} />
    </div>
  )
}
